import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "./icons";
import { Sparkline } from "./charts";
import { pct } from "./format";
import { NAMES, personEmail, personLabel, workspaceLabel, capabilityLabel } from "./data";
import { useFilters } from "./filters";

// ── Surfaces ───────────────────────────────────────────────────────────────

export function Card({ title, subtitle, actions, children, flush = false, style, className = "" }) {
  return (
    <section className={`aa-card aa-fade ${className}`} style={style}>
      {(title || actions) && (
        <header className="aa-card-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && <div className="aa-card-title">{title}</div>}
            {subtitle && <div className="aa-card-sub">{subtitle}</div>}
          </div>
          {actions && <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{actions}</div>}
        </header>
      )}
      <div className={flush ? "aa-card-flush" : "aa-card-body"}>{children}</div>
    </section>
  );
}

/** A page section's heading: what the tab answers, in one line. */
export function PageHeading({ title, children, actions }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 280 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em" }}>{title}</h1>
        {children && <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--aa-muted)", maxWidth: 820 }}>{children}</p>}
      </div>
      {actions}
    </div>
  );
}

export function Info({ text }) {
  if (!text) return null;
  return (
    <span className="aa-info" tabIndex={0} aria-label={text}>
      <Icon name="info" size={13} />
      <span className="aa-info-tip">{text}</span>
    </span>
  );
}

// ── Figures ────────────────────────────────────────────────────────────────

/**
 * The change from the previous period. A rate moves in percentage POINTS
 * (60% → 65% is +5 pts, not +8%); a count moves in percent. `better` says
 * which direction is good; null means neither (credits charged, say).
 */
export function Delta({ cur, prev, better = "up", kind = "count" }) {
  if (cur == null || prev == null || Number.isNaN(Number(cur)) || Number.isNaN(Number(prev))) return null;
  let change;
  let text;
  if (kind === "rate") {
    change = cur - prev;
    text = `${Math.abs(change * 100).toFixed(1)} pts`;
  } else {
    if (!prev) return cur ? <span className="aa-delta aa-delta-flat">new</span> : null;
    change = (cur - prev) / prev;
    text = `${Math.abs(change * 100) >= 100 ? Math.round(Math.abs(change * 100)) : Math.abs(change * 100).toFixed(1)}%`;
  }
  const flat = Math.abs(change) < 0.0005;
  const good = flat || !better ? null : better === "up" ? change > 0 : change < 0;
  const cls = flat || good == null ? "aa-delta-flat" : good ? "aa-delta-good" : "aa-delta-bad";
  return (
    <span className={`aa-delta ${cls}`} title="Change against the previous period of the same length">
      {!flat && <Icon name={change > 0 ? "arrowUp" : "arrowDown"} size={11} strokeWidth={2.5} />}
      {flat ? "0%" : text}
    </span>
  );
}

const STATUS = {
  good: { tone: "good", label: "Healthy" },
  watch: { tone: "warn", label: "Watch" },
  bad: { tone: "bad", label: "Needs attention" },
  none: { tone: "neutral", label: "No data" },
};

export function Pill({ tone = "neutral", dot = false, children, title }) {
  return (
    <span className={`aa-pill aa-pill-${tone}`} title={title}>
      {dot && <span className="aa-dot" />}
      {children}
    </span>
  );
}

export function StatusPill({ status }) {
  const s = STATUS[status] ?? STATUS.none;
  return (
    <Pill tone={s.tone} dot>
      {s.label}
    </Pill>
  );
}

/** A number coloured by its status, with the status in words beside it for anyone who cannot rely on the colour. */
export function Rate({ value, status, format = pct }) {
  const color = status === "good" ? "var(--aa-good-ink)" : status === "bad" ? "var(--aa-bad-ink)" : status === "watch" ? "var(--aa-warn-ink)" : "var(--aa-ink)";
  return (
    <span className="aa-num" style={{ color, fontWeight: 600 }} title={STATUS[status]?.label}>
      {format(value)}
    </span>
  );
}

/**
 * A KPI card: label, the figure, its change against the previous period, and
 * a sparkline of the period. Click-through when it leads somewhere.
 */
export function Kpi({ label, value, info, delta, spark, sparkColor = "var(--aa-s1)", foot, status, onClick, loading }) {
  if (loading) return <KpiSkeleton />;
  return (
    <div className="aa-card aa-kpi aa-fade" data-click={Boolean(onClick)} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
      <div className="aa-kpi-label">
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <Info text={info} />
        {status && status !== "none" && <StatusPill status={status} />}
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div className="aa-kpi-value aa-num" style={{ flex: 1 }}>
          {value}
        </div>
        {spark && spark.length > 1 && (
          <div style={{ width: 96, height: 34, flex: "none" }}>
            <Sparkline values={spark} color={sparkColor} />
          </div>
        )}
      </div>
      <div className="aa-kpi-foot">
        {delta}
        {foot && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{foot}</span>}
      </div>
    </div>
  );
}

// ── States ─────────────────────────────────────────────────────────────────

export function Skeleton({ h = 14, w = "100%", r = 6, style }) {
  return <div className="aa-skel" style={{ height: h, width: w, borderRadius: r, ...style }} />;
}

export function KpiSkeleton() {
  return (
    <div className="aa-card aa-kpi">
      <Skeleton h={12} w="45%" />
      <Skeleton h={28} w="60%" style={{ margin: "6px 0" }} />
      <Skeleton h={12} w="35%" />
    </div>
  );
}

export function ChartSkeleton({ h = 240 }) {
  return (
    <div style={{ height: h, display: "flex", alignItems: "flex-end", gap: 6, padding: "8px 0" }}>
      {Array.from({ length: 24 }, (_, i) => (
        <div key={i} className="aa-skel" style={{ flex: 1, height: `${25 + ((i * 37) % 60)}%` }} />
      ))}
    </div>
  );
}

export function RowsSkeleton({ rows = 6 }) {
  return (
    <div style={{ display: "grid", gap: 14, padding: "8px 20px 20px" }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: "flex", gap: 16 }}>
          <Skeleton w="32%" />
          <Skeleton w="18%" />
          <Skeleton w="14%" />
          <Skeleton w="20%" />
        </div>
      ))}
    </div>
  );
}

export function Empty({ icon = "inbox", title = "Nothing here yet", children, compact = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: compact ? "20px 16px" : "40px 16px", color: "var(--aa-muted)" }}>
      <div className="aa-icon-tile" style={{ width: 40, height: 40, borderRadius: 12, background: "var(--aa-surface-3)", color: "var(--aa-muted)", marginBottom: 12 }}>
        <Icon name={icon} size={20} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--aa-ink)" }}>{title}</div>
      {children && <div style={{ fontSize: 13, marginTop: 4, maxWidth: 420 }}>{children}</div>}
    </div>
  );
}

export function ErrorState({ error }) {
  return (
    <Empty icon="alert" title="Could not load this view">
      {error}
    </Empty>
  );
}

export function Banner({ tone = "warn", icon = "alert", title, children, action }) {
  return (
    <div
      className="aa-fade"
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        padding: "12px 16px",
        borderRadius: 12,
        border: `1px solid var(--aa-${tone}-soft)`,
        background: `var(--aa-${tone}-soft)`,
        marginBottom: 20,
        fontSize: 13,
      }}
    >
      <span style={{ color: `var(--aa-${tone}-ink)`, marginTop: 1 }}>
        <Icon name={icon} size={16} />
      </span>
      <div style={{ flex: 1 }}>
        {title && <div style={{ fontWeight: 600, marginBottom: 2 }}>{title}</div>}
        <div style={{ color: "var(--aa-ink-2)" }}>{children}</div>
      </div>
      {action}
    </div>
  );
}

// ── Controls ───────────────────────────────────────────────────────────────

export function Segmented({ options, value, onChange, label }) {
  return (
    <div className="aa-seg" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

export function Select({ value, onChange, options, width, label }) {
  return (
    <select className="aa-input aa-select" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} style={{ width }}>
      {options.map(([v, text]) => (
        <option key={v} value={v}>
          {text}
        </option>
      ))}
    </select>
  );
}

export function SearchInput({ value, onChange, placeholder, width = 260 }) {
  return (
    <div style={{ position: "relative", width }}>
      <span style={{ position: "absolute", left: 10, top: 9, color: "var(--aa-faint)" }}>
        <Icon name="search" size={15} />
      </span>
      <input className="aa-input" type="search" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={{ width: "100%", paddingLeft: 32 }} />
    </div>
  );
}

/** A labelled filter control inside a toolbar. */
export function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: "var(--aa-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

export function Toggle({ on, onChange, children }) {
  return (
    <button type="button" className={`aa-btn aa-btn-sm`} onClick={() => onChange(!on)} style={on ? { background: "var(--aa-accent-soft)", borderColor: "var(--aa-accent)", color: "var(--aa-accent-ink)" } : undefined}>
      {on && <Icon name="check" size={13} strokeWidth={2.5} />}
      {children}
    </button>
  );
}

export function Chip({ k, v, onRemove, title }) {
  return (
    <span className="aa-chip aa-fade" title={title}>
      <span className="aa-chip-key">{k}</span>
      <span className="aa-chip-val">{v}</span>
      <button type="button" onClick={onRemove} aria-label={`Remove ${k} filter`}>
        <Icon name="x" size={13} />
      </button>
    </span>
  );
}

/** Close a popover on an outside click or Escape. */
export function useDismiss(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const click = (e) => ref.current && !ref.current.contains(e.target) && onClose();
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", click);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", click);
      document.removeEventListener("keydown", key);
    };
  }, [open, onClose]);
  return ref;
}

// ── Table ──────────────────────────────────────────────────────────────────

/**
 * A data table: sticky header, click-to-sort columns (a column sorts by its
 * `sort` accessor, else its key), hover rows, and optional row click.
 */
export function Table({ columns, rows, onRow, rowKey = (r, i) => r.id ?? i, defaultSort, maxHeight, selectedKey }) {
  const [sort, setSort] = useState(defaultSort ?? null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const get = col.sort ?? ((r) => r[col.key]);
    return [...rows].sort((a, b) => {
      const x = get(a);
      const y = get(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const d = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? d : -d;
    });
  }, [rows, sort, columns]);
  const toggle = (col) => {
    if (col.sortable === false) return;
    setSort((s) => (s?.key === col.key ? { key: col.key, dir: s.dir === "desc" ? "asc" : "desc" } : { key: col.key, dir: col.align === "right" ? "desc" : "asc" }));
  };
  return (
    <div className="aa-table-wrap" style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
      <table className="aa-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={col.align === "right" ? "aa-r" : undefined}
                data-sort={col.sortable === false ? undefined : "true"}
                onClick={() => toggle(col)}
                style={{ width: col.width }}
                title={col.help}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {col.label}
                  {sort?.key === col.key && <Icon name={sort.dir === "asc" ? "arrowUp" : "arrowDown"} size={11} strokeWidth={2.5} />}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr
              key={rowKey(r, i)}
              data-click={Boolean(onRow)}
              data-selected={selectedKey != null && rowKey(r, i) === selectedKey ? "true" : undefined}
              onClick={onRow ? () => onRow(r) : undefined}
            >
              {columns.map((col) => (
                <td key={col.key} className={col.align === "right" ? "aa-r" : undefined} style={{ maxWidth: col.max, whiteSpace: col.nowrap ? "nowrap" : undefined }}>
                  {col.render ? col.render(r) : r[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Entities (a click on one fills the page filter) ───────────────────────

/** Stop a click on an entity inside a clickable row from also opening the row. */
const own = (fn) => (e) => {
  e.stopPropagation();
  fn();
};

export function Avatar({ text, size = 24 }) {
  const initials = String(text ?? "?")
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  let h = 0;
  for (const ch of String(text ?? "")) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        flex: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.42,
        fontWeight: 600,
        color: `hsl(${h} 45% 32%)`,
        background: `hsl(${h} 70% 90%)`,
      }}
    >
      {initials || "?"}
    </span>
  );
}

/** A workspace by name. Clicking it filters the whole page to that workspace. */
export function Workspace({ id, filter = true, sub = false }) {
  const { set } = useFilters();
  const n = NAMES.accounts.get(id);
  const hint = [n?.slug, id].filter(Boolean).join(" · ");
  if (!id) return <span style={{ color: "var(--aa-faint)" }}>—</span>;
  const label = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <span className="aa-icon-tile" style={{ width: 22, height: 22, borderRadius: 6, background: "var(--aa-surface-3)", color: "var(--aa-muted)" }}>
        <Icon name="workspaces" size={13} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 500 }}>{workspaceLabel(id)}</span>
        {sub && n?.slug && n?.name && <span className="aa-sub">{n.slug}</span>}
      </span>
    </span>
  );
  if (!filter) return <span title={hint}>{label}</span>;
  return (
    <button type="button" className="aa-entity" title={`Filter to this workspace · ${hint}`} onClick={own(() => set({ ws: id }))}>
      {label}
    </button>
  );
}

/** A person by name with their email under it. Clicking filters people and conversations to them. */
export function Person({ id, filter = true, compact = false }) {
  const { set } = useFilters();
  if (!id) return <span style={{ color: "var(--aa-faint)" }}>—</span>;
  const email = personEmail(id);
  const name = personLabel(id);
  const label = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <Avatar text={name} size={compact ? 20 : 24} />
      <span style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 500 }}>{name}</span>
        {!compact && email && email !== name && <span className="aa-sub">{email}</span>}
      </span>
    </span>
  );
  if (!filter) return label;
  return (
    <button type="button" className="aa-entity" title={`Filter to this person · ${[email, id].filter(Boolean).join(" · ")}`} onClick={own(() => set({ person: id }))}>
      {label}
    </button>
  );
}

/** A capability by its own name, with its id beneath. */
export function Capability({ id, withId = true }) {
  const info = NAMES.capabilities.get(id);
  if (id === "unattributed") return <span style={{ color: "var(--aa-muted)" }}>Recorded before capabilities were labelled</span>;
  return (
    <span title={[info?.summary, id].filter(Boolean).join(" · ")}>
      <span style={{ fontWeight: 500 }}>{capabilityLabel(id)}</span>
      {withId && <span className="aa-sub aa-mono">{id}</span>}
    </span>
  );
}

